import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowRight, ArrowLeft } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TourStep {
  target?: string;
  title: string;
  emoji: string;
  body: string;
  placement: 'right' | 'left' | 'top' | 'bottom' | 'center';
}

interface SpotRect { top: number; left: number; width: number; height: number }

interface Props {
  steps: TourStep[];
  onComplete: () => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PAD  = 8;
const TW   = 300;
const GAP  = 16;
const NAVY = 'var(--primary)';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function measure(selector: string): SpotRect | null {
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 };
}

function rectsEqual(a: SpotRect | null, b: SpotRect | null) {
  if (!a || !b) return a === b;
  return a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;
}

function tipPos(spot: SpotRect, placement: TourStep['placement']): React.CSSProperties {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (placement === 'right')
    return { top: Math.max(16, Math.min(spot.top, vh - 320)), left: Math.min(spot.left + spot.width + GAP, vw - TW - 16) };
  if (placement === 'left')
    return { top: Math.max(16, Math.min(spot.top, vh - 320)), left: Math.max(16, spot.left - TW - GAP) };
  if (placement === 'bottom')
    return { top: spot.top + spot.height + GAP, left: Math.max(16, Math.min(spot.left, vw - TW - 16)) };
  return { top: Math.max(16, spot.top - 320 - GAP), left: Math.max(16, Math.min(spot.left, vw - TW - 16)) };
}

function Caret({ placement }: { placement: TourStep['placement'] }) {
  if (placement === 'center') return null;
  const S = 10;
  const base: React.CSSProperties = { position: 'absolute', width: 0, height: 0, borderStyle: 'solid', pointerEvents: 'none', zIndex: 1 };
  if (placement === 'right')  return <div style={{ ...base, left: -S, top: 28, borderWidth: `${S}px ${S}px ${S}px 0`, borderColor: `transparent var(--card) transparent transparent` }} />;
  if (placement === 'left')   return <div style={{ ...base, right: -S, top: 28, borderWidth: `${S}px 0 ${S}px ${S}px`, borderColor: `transparent transparent transparent var(--card)` }} />;
  if (placement === 'bottom') return <div style={{ ...base, top: -S, left: 28, borderWidth: `0 ${S}px ${S}px ${S}px`, borderColor: `transparent transparent var(--card) transparent` }} />;
  return <div style={{ ...base, bottom: -S, left: 28, borderWidth: `${S}px ${S}px 0 ${S}px`, borderColor: `var(--card) transparent transparent transparent` }} />;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function PageTour({ steps, onComplete }: Props) {
  const [idx, setIdx]     = useState(0);
  const [spot, setSpot]   = useState<SpotRect | null>(null);
  const [animKey, setAnimKey] = useState(0);
  const prevSpot = useRef<SpotRect | null>(null);

  const step    = steps[idx]!;
  const isFirst = idx === 0;
  const isLast  = idx === steps.length - 1;

  // Poll to keep spotlight in sync with element position
  useEffect(() => {
    if (!step.target) {
      setSpot(null);
      prevSpot.current = null;
      return;
    }
    let rafId: number;
    let tick = 0;
    function poll() {
      if (tick++ % 6 === 0) {
        const r = measure(step.target!);
        if (r && !rectsEqual(r, prevSpot.current)) {
          prevSpot.current = r;
          setSpot(r);
        }
      }
      rafId = requestAnimationFrame(poll);
    }
    rafId = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(rafId);
  }, [idx, step.target]);

  useEffect(() => { setAnimKey(k => k + 1); }, [idx]);

  const next = useCallback(() => { if (isLast) { onComplete(); return; } setIdx(i => i + 1); }, [isLast, onComplete]);
  const prev = useCallback(() => { if (!isFirst) setIdx(i => i - 1); }, [isFirst]);
  const skip = useCallback(() => onComplete(), [onComplete]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowRight' || e.key === 'Enter') next();
      if (e.key === 'ArrowLeft')  prev();
      if (e.key === 'Escape')     skip();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, skip]);

  const tooltipStyle: React.CSSProperties = spot
    ? { position: 'fixed', ...tipPos(spot, step.placement) }
    : { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' };

  return createPortal(
    <>
      <style>{`
        @keyframes page-tour-tip-in {
          from { opacity:0; transform:translateY(8px); }
          to   { opacity:1; transform:translateY(0);   }
        }
        @keyframes page-tour-pulse {
          0%,100% { outline-color: rgba(255,255,255,0.55); }
          50%      { outline-color: rgba(255,255,255,0.95); }
        }
      `}</style>

      {/* Backdrop for centre steps */}
      {!spot && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99990, backgroundColor: 'rgba(0,0,0,0.55)', pointerEvents: 'none' }} />
      )}

      {/* Spotlight */}
      {spot && (
        <div style={{
          position: 'fixed', zIndex: 99990,
          top: spot.top, left: spot.left, width: spot.width, height: spot.height,
          borderRadius: 10,
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.58)',
          outline: '3px solid rgba(255,255,255,0.7)', outlineOffset: 2,
          animation: 'page-tour-pulse 2.2s ease-in-out infinite',
          transition: 'top 0.35s cubic-bezier(.4,0,.2,1), left 0.35s cubic-bezier(.4,0,.2,1), width 0.35s cubic-bezier(.4,0,.2,1), height 0.35s cubic-bezier(.4,0,.2,1)',
          pointerEvents: 'none',
        }} />
      )}

      {/* Tooltip card */}
      <div
        key={animKey}
        style={{
          zIndex: 100000, width: TW, pointerEvents: 'all',
          animation: 'page-tour-tip-in 0.22s ease',
          position: 'fixed',
          ...(step.placement === 'center' ? { top: '50%', left: '50%', transform: 'translate(-50%,-50%)' } : tooltipStyle),
        }}
      >
        <div style={{ position: 'relative', height: 0 }}>
          <Caret placement={step.placement} />
        </div>
        <div style={{ backgroundColor: 'var(--card)', borderRadius: 16, boxShadow: '0 20px 56px rgba(0,0,0,0.38)', overflow: 'hidden' }}>
          <div style={{ height: 4, background: `linear-gradient(90deg,${NAVY},var(--chart-1))` }} />
          <div style={{ padding: '16px 18px 14px' }}>
            {/* Progress dots + close */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 4 }}>
                {steps.map((_, i) => (
                  <div key={i} style={{ height: 6, width: i === idx ? 18 : 6, borderRadius: 3, transition: 'all 0.2s', backgroundColor: i === idx ? NAVY : i < idx ? 'color-mix(in oklab, var(--primary) 35%, var(--background))' : 'var(--border)' }} />
                ))}
              </div>
              <button onClick={skip} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted-foreground)', padding: 2, display: 'flex', lineHeight: 1 }}>
                <X size={14} />
              </button>
            </div>

            <div style={{ fontSize: 26, marginBottom: 6 }}>{step.emoji}</div>
            <h3 style={{ fontFamily: '"DM Sans",sans-serif', fontWeight: 700, fontSize: 16, color: NAVY, margin: '0 0 7px' }}>
              {step.title}
            </h3>
            <p style={{ fontFamily: '"Inter",sans-serif', fontSize: 13, color: 'var(--muted-foreground)', margin: '0 0 16px', lineHeight: 1.6 }}>
              {step.body}
            </p>

            <div style={{ display: 'flex', gap: 8 }}>
              {!isFirst && (
                <button onClick={prev} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border)', background: 'transparent', fontFamily: '"Inter",sans-serif', fontSize: 13, fontWeight: 500, color: 'var(--muted-foreground)', cursor: 'pointer', flexShrink: 0 }}>
                  <ArrowLeft size={13} /> Back
                </button>
              )}
              <button onClick={next} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 12px', borderRadius: 8, border: 'none', background: NAVY, fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--primary-foreground)', cursor: 'pointer' }}>
                {isLast ? 'Done!' : 'Next'}{!isLast && <ArrowRight size={13} />}
              </button>
            </div>

            {!isLast && (
              <button onClick={skip} style={{ display: 'block', width: '100%', marginTop: 8, background: 'none', border: 'none', fontFamily: '"Inter",sans-serif', fontSize: 11, color: 'var(--muted-foreground)', cursor: 'pointer', textAlign: 'center' }}>
                Skip  (Esc)
              </button>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
